import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Contractor,
  InspectionModel,
  Planner,
  SummaryNotes
} from '@authority-labs/api-interfaces';
import { PERMISSION_KEYS } from '@authority-labs/constants';
import { InspectionProperty } from '@authority-labs/enums';
import { AuthenticationService } from '@check-list/app/auth/authentication.service';
import { ComponentCanDeactivate } from '@check-list/app/classes';
import { AppPermissionsService } from '@check-list/app/shared/services/permissions/permissions.service';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { InspectionsService } from '../inspections.service';
import { isInspectionReadonly } from '../shared/inspection-utils';
import { AutoSaveStatus } from './components/draft-toolbar/draft-toolbar.component';
import { isInkNote } from './models/draft-form.model';
import { DraftStateService } from './services/draft-state.service';

const LS_KEY = 'draft-backup';

@Component({
  selector: 'authority-labs-inspection-draft',
  templateUrl: './inspection-draft.component.html',
  styleUrls: ['./inspection-draft.component.scss'],
  providers: [DraftStateService]
})
export class InspectionDraftComponent
  extends ComponentCanDeactivate
  implements OnInit, OnDestroy
{
  entity: InspectionModel;
  loading = true;
  readonly = false;
  canReopen = false;
  draftClosed = false;
  autoSaveStatus: AutoSaveStatus = null;
  recognizing = false;

  @ViewChild('inspectionDesignTpl', { static: false })
  inspectionDesignTpl: TemplateRef<unknown>;
  @ViewChild('inspectionInstallationTpl', { static: false })
  inspectionInstallationTpl: TemplateRef<unknown>;
  @ViewChild('inspectionWaterNetworkCharacterizationTpl', { static: false })
  inspectionWaterNetworkCharacterizationTpl: TemplateRef<unknown>;
  @ViewChild('inspectionGasExtinguishingSystemTpl', { static: false })
  inspectionGasExtinguishingSystemTpl: TemplateRef<unknown>;
  @ViewChild('inspectionFireTapsTpl', { static: false })
  inspectionFireTapsTpl: TemplateRef<unknown>;
  @ViewChild('inspectionFireExtinguishersTpl', { static: false })
  inspectionFireExtinguishersTpl: TemplateRef<unknown>;
  @ViewChild('inspectionAerosolShutdownTpl', { static: false })
  inspectionAerosolShutdownTpl: TemplateRef<unknown>;
  @ViewChild('inspectionFireAlarmSystemTpl', { static: false })
  inspectionFireAlarmSystemTpl: TemplateRef<unknown>;
  @ViewChild('inspectionAirConditioningAndVentilationSystemsTpl', {
    static: false
  })
  inspectionAirConditioningAndVentilationSystemsTpl: TemplateRef<unknown>;
  @ViewChild('inspectionVentilationSystemAndFireProtectionTpl', {
    static: false
  })
  inspectionVentilationSystemAndFireProtectionTpl: TemplateRef<unknown>;
  @ViewChild('inspectionPressureSystemInStairwellsCompatibilityCheckTpl', {
    static: false
  })
  inspectionPressureSystemInStairwellsCompatibilityCheckTpl: TemplateRef<unknown>;
  @ViewChild('inspectionIntegrationTpl', { static: false })
  inspectionIntegrationTpl: TemplateRef<unknown>;

  sectionItems: {
    inspectionKey: InspectionProperty;
    title: string;
    template: TemplateRef<unknown>;
  }[] = [];

  private destroy$ = new Subject<void>();
  private formChanged$ = new Subject<void>();
  private formDirty = false;
  private statusTimer: string | number | NodeJS.Timeout;

  get title(): string {
    return `טיוטה עבור הזמנה מס' ${this.entity.orderId} ( ${
      this.entity.order?.subProject?.name || ''
    } )`;
  }

  get canFinishDraft(): boolean {
    if (!this.entity) return false;
    const disclaimer = this.entity.disclaimer;
    if (disclaimer?.isThereConflictOfInterest == null) return false;
    if (disclaimer?.isThereReasonForBias == null) return false;
    const signatureStrokes = this.draftState.getStrokes(
      'general:disclaimerSignature'
    );
    if (!signatureStrokes || signatureStrokes.length === 0) return false;
    return true;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private inspectionsService: InspectionsService,
    public draftState: DraftStateService,
    private message: NzMessageService,
    private modal: NzModalService,
    private authService: AuthenticationService,
    private appPermissionsService: AppPermissionsService
  ) {
    super();
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.readonly || (!this.draftState.isDirty && !this.formDirty);
  }

  ngOnInit(): void {
    const id = +this.route.snapshot.params.id;

    const query = RequestQueryBuilder.create({
      join: [
        { field: 'order' },
        { field: 'order.status' },
        { field: 'order.planner' },
        { field: 'order.contractor' },
        { field: 'order.subProject' }
      ]
    }).query();

    forkJoin([
      this.inspectionsService.getOne(id, query),
      this.inspectionsService.getDraftData(id)
    ]).subscribe(([inspection, draftResponse]) => {
      // Use saved draft snapshot if available, otherwise use fresh inspection
      const baseData = draftResponse.inspectionData || inspection;
      this.entity = new InspectionModel(baseData);
      // Always use fresh order data from API (snapshot may not include full order relations)
      this.entity.order = inspection.order ?? this.entity.order ?? {};
      this.entity.order.planner = this.entity.order?.planner ?? new Planner();
      this.entity.order.contractor =
        this.entity.order?.contractor ?? new Contractor();

      // Compute readonly: draft status + order-based logic
      const hasManagePermission = this.appPermissionsService.has(
        PERMISSION_KEYS.INSPECTIONS_MANAGE
      );
      this.draftClosed = draftResponse.status === 'closed';
      this.readonly =
        this.draftClosed ||
        isInspectionReadonly(
          hasManagePermission,
          this.entity?.order,
          this.authService.getUser()
        );
      const currentUser = this.authService.getUsername();
      const isApprover = currentUser === this.entity.order?.approvedBy;
      this.canReopen =
        this.draftClosed &&
        (isApprover ||
          this.appPermissionsService.has(PERMISSION_KEYS.INSPECTIONS_REOPEN));

      // Load ink data, then check localStorage for newer backup
      if (draftResponse.inkFormData) {
        this.draftState.loadInkData(draftResponse.inkFormData);
      }
      this.restoreFromLocalStorage(id);

      this.loading = false;

      // Save initial snapshot only if there wasn't one already
      if (!draftResponse.inspectionData) {
        this.inspectionsService
          .saveDraftSnapshot(this.entity.id, inspection)
          .subscribe();
      }

      // Build section list after template refs are available
      setTimeout(() => this.setSections());
    });

    // Immediate localStorage backup on every stroke
    this.draftState.strokeAdded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.backupToLocalStorage());

    // Auto-save 1s after user stops drawing
    this.draftState.strokeAdded$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(1000),
        filter(() => !this.readonly && this.draftState.hasDirtyFields),
        tap(() => this.setAutoSaveStatus('saving')),
        switchMap(() => this.saveDraftData())
      )
      .subscribe({
        next: () => {
          this.clearLocalStorage();
          this.setAutoSaveStatus('saved');
        },
        error: () => {
          this.setAutoSaveStatus('error');
        }
      });

    // Auto-save form data 2s after user stops changing fields
    this.formChanged$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(2000),
        filter(() => !this.readonly),
        tap(() => this.setAutoSaveStatus('saving')),
        switchMap(() =>
          this.inspectionsService
            .saveDraftSnapshot(this.entity.id, this.entity)
            .pipe(
              catchError(() => {
                this.setAutoSaveStatus('error');
                return of(null);
              })
            )
        )
      )
      .subscribe((result) => {
        if (result !== null) {
          this.formDirty = false;
          this.setAutoSaveStatus('saved');
        }
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.statusTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDataChange(): void {
    if (this.readonly) return;
    this.formDirty = true;
    this.formChanged$.next();
  }

  onFormFieldChange(): void {
    if (this.readonly) return;
    this.formDirty = true;
    this.formChanged$.next();
  }

  onSummaryNotesChange(
    summaryNotes: SummaryNotes,
    entity: { summaryNotes: SummaryNotes }
  ): void {
    entity.summaryNotes = summaryNotes;
    if (!this.readonly) {
      this.formDirty = true;
      this.formChanged$.next();
    }
  }

  undo(): void {
    this.draftState.undo();
  }

  get hasInkData(): boolean {
    const inkData = this.draftState.serializeInkData();
    const hasOverlayStrokes = inkData?.fields
      ? Object.values(inkData.fields).some(
          (f) => f.strokes && f.strokes.length > 0
        )
      : false;
    if (hasOverlayStrokes) return true;

    // Also check for committed ink notes in the entity data
    return this.hasCommittedInkNotes();
  }

  /** Recursively check if any notes array in the entity contains ink notes */
  private hasCommittedInkNotes(): boolean {
    if (!this.entity) return false;
    const visited = new Set<unknown>();

    const hasInkInNotes = (notes: unknown): boolean =>
      Array.isArray(notes) &&
      notes.some(
        (n: Record<string, unknown>) =>
          (n?.notes && typeof n.notes === 'string' && isInkNote(n.notes)) ||
          (Array.isArray(n?.subNotes) && hasInkInNotes(n.subNotes))
      );

    const walk = (obj: unknown): boolean => {
      if (!obj || typeof obj !== 'object' || visited.has(obj)) return false;
      visited.add(obj);
      const o = obj as Record<string, unknown>;
      if (hasInkInNotes(o.notes)) return true;
      if (hasInkInNotes(o.multiNotes)) return true;
      if (hasInkInNotes(o.summaryNotes)) return true;
      for (const key of Object.keys(o)) {
        if (key === 'notes' || key === 'multiNotes' || key === 'summaryNotes')
          continue;
        const val = o[key];
        if (Array.isArray(val)) {
          for (const item of val) {
            if (walk(item)) return true;
          }
        } else if (val && typeof val === 'object') {
          if (walk(val)) return true;
        }
      }
      return false;
    };

    return walk(this.entity);
  }

  finishDraft(): void {
    this.modal.confirm({
      nzTitle: 'סיום ונעילת הטיוטה',
      nzContent: 'לאחר נעילת הטיוטה לא ניתן יהיה לערוך אותה. האם להמשיך?',
      nzOkText: 'נעל',
      nzOkDanger: true,
      nzCancelText: 'ביטול',
      nzOnOk: () => {
        this.inspectionsService
          .saveDraftSnapshot(this.entity.id, this.entity)
          .pipe(
            switchMap(() =>
              this.inspectionsService.updateDraftStatus(
                this.entity.id,
                'closed'
              )
            )
          )
          .subscribe(() => {
            this.draftClosed = true;
            this.readonly = true;
            const currentUser = this.authService.getUsername();
            this.canReopen =
              currentUser === this.entity.order?.approvedBy ||
              this.appPermissionsService.has(
                PERMISSION_KEYS.INSPECTIONS_REOPEN
              );
            this.message.success('הטיוטה ננעלה');
          });
      }
    });
  }

  reopenDraft(): void {
    this.inspectionsService
      .updateDraftStatus(this.entity.id, 'open')
      .subscribe(() => {
        this.draftClosed = false;
        this.readonly = false;
        this.canReopen = false;
        this.message.success('הטיוטה נפתחה מחדש');
      });
  }

  private saveDraftData() {
    if (!this.draftState.hasDirtyFields) {
      return of(null);
    }
    const draftData = this.draftState.serializeDelta();
    return this.inspectionsService
      .patchDraftData(this.entity.id, draftData)
      .pipe(tap(() => this.draftState.markClean()));
  }

  private setAutoSaveStatus(status: AutoSaveStatus): void {
    clearTimeout(this.statusTimer);
    this.autoSaveStatus = status;
    if (status === 'saved') {
      this.statusTimer = setTimeout(() => {
        this.autoSaveStatus = null;
      }, 2000);
    }
  }

  // --- localStorage backup ---

  private backupToLocalStorage(): void {
    try {
      const backup = {
        inspectionId: this.entity.id,
        timestamp: Date.now(),
        draftData: this.draftState.serializeInkData()
      };
      localStorage.setItem(LS_KEY, JSON.stringify(backup));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }

  private restoreFromLocalStorage(inspectionId: number): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;

      const backup = JSON.parse(raw);
      if (backup.inspectionId !== inspectionId) {
        // Different inspection — discard old backup
        localStorage.removeItem(LS_KEY);
        return;
      }

      // Merge backup fields into current state
      const backupData = backup.draftData || backup.inkFormData;
      if (backupData?.fields) {
        const currentData = this.draftState.serializeInkData();
        let hasNewData = false;

        for (const [fieldId, fieldData] of Object.entries(backupData.fields)) {
          const currentStrokes = currentData.fields[fieldId]?.strokes || [];
          const backupStrokes = (fieldData as any)?.strokes || [];
          if (backupStrokes.length > currentStrokes.length) {
            hasNewData = true;
          }
        }

        if (hasNewData) {
          this.draftState.loadInkData(backupData);
          this.message.info('שוחזרו נתונים מגיבוי מקומי');
        }
      }

      localStorage.removeItem(LS_KEY);
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }

  private clearLocalStorage(): void {
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      // ignore
    }
  }

  private setSections(): void {
    const activeKeys = this.entity.getActiveInspectionsKeys();
    this.sectionItems = activeKeys.map((key) => ({
      inspectionKey: key as InspectionProperty,
      title: this.entity[key]?.title || key,
      template: this[`${key}Tpl`]
    }));
  }
}
